import { ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '../utils/cn';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
}

/**
 * Button - Accessible button component with loading states
 * 
 * Required CSS classes:
 * - .btn - Base button styles
 * - .btn-primary, .btn-secondary, .btn-ghost, .btn-danger - Variants
 * - .btn-sm, .btn-md, .btn-lg - Sizes
 * - .btn-loading - Loading state
 * - .spinner-sm - Loading spinner
 * 
 * Accessibility:
 * - Keyboard: Tab (focus), Enter/Space (activate)
 * - Screen reader: Announces loading state via aria-busy
 * - Focus: Visible focus indicator
 * 
 * @example
 * <Button variant="primary" size="lg" loading={isLoading}>
 *   Submit
 * </Button>
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', loading, children, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          'btn',
          `btn-${variant}`,
          `btn-${size}`,
          loading && 'btn-loading',
          className
        )}
        disabled={disabled || loading}
        aria-busy={loading}
        aria-disabled={disabled || loading}
        {...props}
      >
        {loading ? <span className="spinner-sm" aria-hidden="true" /> : children}
      </button>
    );
  }
);

Button.displayName = 'Button';
